// Tab 1 · Políticas Estándar: barra de segmentación SCI + master-detail de planes por nivel.
// CONECTADO a loan-offerings: `plans` es la verdad del server (query de usePolicies); el estado
// local es SOLO el buffer de edición (`draft`, `segCuts`). Publicar dispara la mutación (PUT) y la
// invalidación relee el server → los efectos de resync sincronizan el draft/los cortes con la
// verdad (incluida la nueva `version` que asigna el backend). Un refetch de fondo que no cambia la
// data no re-dispara los efectos (referencias estables) → no pisa ediciones en curso.
// Publicar la segmentación escribe DOS fuentes: el rango SCI de cada plan y los cortes del motor
// de score (loan-config). `engineCuts` es la lectura de esos cortes, para avisar si difieren.
import { useEffect, useId, useMemo, useState } from 'react';
import { Wrench } from 'lucide-react';
import { ConfirmDialog } from '@/kit';
import { AUDIT_REASON_MAX_LENGTH } from '@/api/services/loan-offerings/contract';
import { userMessageOf } from '@/api/errors';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/cn';
import { MasterDetail, MasterDetailEmpty } from '@/shared/patterns/MasterDetail';
import { formatDateMx } from '@/lib/dateHelpers';
import { formatMXN } from '@/shared/lib/format/money';
import { TierPill } from '@/shared/patterns/Pills';
import { usePublishSegmentation, useUpdateCreditPlan, type SegmentEdit } from '../api/usePolicies';
import { planTierToLevel, prepaymentMinimumLabel } from '../logic';
import { PlanDetail } from './PlanDetail';
import { SciSegmentBar, type SciCuts } from './SciSegmentBar';
import { TIER_LABEL, type CreditPlan, type StandardTier } from '../types';

const TIER_ORDER: Record<string, number> = { bronce: 1, plata: 2, oro: 3 };

type SortCol = 'tier' | 'tna' | 'amount' | 'version';

const STANDARD_TIERS: StandardTier[] = ['bronce', 'plata', 'oro'];

/** Cortes de fallback: solo para poder DIBUJAR la barra cuando falta el plan de un nivel. */
const FALLBACK_CUTS: SciCuts = { start: 21, bp: 61, po: 81 };

/** Niveles del segmento Estándar que hoy no tienen plan en el catálogo. */
export function missingTiers(plans: CreditPlan[]): StandardTier[] {
    return STANDARD_TIERS.filter((tier) => !plans.some((p) => p.tier === tier));
}

function deriveCuts(plans: CreditPlan[]): SciCuts {
    const min = (tier: StandardTier, fallback: number): number =>
        plans.find((p) => p.tier === tier)?.sciMin ?? fallback;
    return {
        start: min('bronce', FALLBACK_CUTS.start),
        bp: min('plata', FALLBACK_CUTS.bp),
        po: min('oro', FALLBACK_CUTS.po),
    };
}

/** Los tres cortes tal como se leen en el aviso: «21 · 61 · 81». */
export function cutsLabel(cuts: SciCuts): string {
    return `${String(cuts.start)} · ${String(cuts.bp)} · ${String(cuts.po)}`;
}

/** Los cortes del motor de score y los de los planes apuntan al mismo lugar. */
export function sameCuts(a: SciCuts, b: SciCuts): boolean {
    return a.start === b.start && a.bp === b.bp && a.po === b.po;
}

/**
 * Rango que le tocaría a cada nivel con los cortes de la barra. El techo de Oro NO es 100 fijo: es
 * el `maxSciScore` del propio plan, porque la barra no tiene handle para el tope y clavarlo en 100
 * lo marcaba como afectado y lo pisaba al publicar.
 */
export function cutsMapping(
    cuts: SciCuts,
    plans: CreditPlan[],
): Record<StandardTier, { min: number; max: number }> {
    const oroMax = plans.find((p) => p.tier === 'oro')?.sciMax ?? 100;
    return {
        bronce: { min: cuts.start, max: cuts.bp - 1 },
        plata: { min: cuts.bp, max: cuts.po - 1 },
        oro: { min: cuts.po, max: oroMax },
    };
}

/**
 * Planes a los que el PUT de la segmentación les cambiaría el rango SCI. Un plan INACTIVE queda
 * fuera: el wizard no lo ofrece, así que reescribirle los cortes es tocar catálogo muerto.
 */
export function plansAffectedBy(
    cuts: SciCuts,
    plans: CreditPlan[],
): (CreditPlan & { tier: StandardTier })[] {
    const mapping = cutsMapping(cuts, plans);
    return plans
        .filter((p): p is CreditPlan & { tier: StandardTier } => p.tier !== 'empleado')
        .filter(
            (p) =>
                p.active && (mapping[p.tier].min !== p.sciMin || mapping[p.tier].max !== p.sciMax),
        );
}

export function StandardPoliciesTab({
    plans,
    engineCuts,
}: {
    plans: CreditPlan[];
    /** Cortes vigentes del motor de score; `null` si no se pudieron leer los parámetros. */
    engineCuts: SciCuts | null;
}) {
    const updateMut = useUpdateCreditPlan();
    const segMut = usePublishSegmentation();

    const [sort, setSort] = useState<{ col: SortCol; dir: 'asc' | 'desc' }>({
        col: 'tier',
        dir: 'asc',
    });
    // Selección por planId, NO por tier: el backend permite varios planes del mismo nivel (unicidad
    // por `code`), así que puede haber 2 "Plata". Seleccionar por tier resaltaba/keyaba ambos.
    const initialPlanId =
        (plans.find((p) => p.tier === 'bronce') ?? plans.find((p) => p.tier !== 'empleado'))
            ?.planId ?? null;
    const [selectedPlanId, setSelectedPlanId] = useState<string | null>(initialPlanId);
    const [draft, setDraft] = useState<CreditPlan | null>(
        () => plans.find((p) => p.planId === initialPlanId) ?? null,
    );
    const [resetCount, setResetCount] = useState(0);
    const [segCuts, setSegCuts] = useState<SciCuts>(() => deriveCuts(plans));
    const [confirm, setConfirm] = useState<'plan' | 'seg' | null>(null);
    // Un solo buffer para los dos diálogos: nunca hay dos abiertos, y cerrar lo vacía para que el
    // motivo de una publicación no reaparezca escrito en la siguiente.
    const [reason, setReason] = useState('');

    const closeConfirm = (): void => {
        setConfirm(null);
        setReason('');
    };

    // El plan Empleado no vive en el segmento Estándar (es política del segmento Empleados).
    const standardPlans = plans.filter(
        (p): p is CreditPlan & { tier: StandardTier } => p.tier !== 'empleado',
    );

    // Plan del server seleccionado (referencia estable vía structural-sharing). La identidad es el
    // planId, no el tier: el backend permite varios planes del mismo nivel.
    const serverPlan = useMemo(
        () => plans.find((p) => p.planId === selectedPlanId) ?? null,
        [plans, selectedPlanId],
    );
    const dirty = !!draft && !!serverPlan && JSON.stringify(draft) !== JSON.stringify(serverPlan);

    // Resync del draft con el server: cambia al elegir otro nivel o cuando el plan cambia en el
    // server (p.ej. tras publicar: version++). Deps = [serverPlan] (estable) → no pisa ediciones.
    useEffect(() => {
        setDraft(serverPlan ? { ...serverPlan, termsWeeks: [...serverPlan.termsWeeks] } : null);
        setResetCount((c) => c + 1);
    }, [serverPlan]);

    // ---- Barra de segmentación ----
    const serverCuts = useMemo(() => deriveCuts(plans), [plans]);
    const segDirty = JSON.stringify(segCuts) !== JSON.stringify(serverCuts);
    const mapping = cutsMapping(segCuts, plans);
    const tiersSinPlan = useMemo(() => missingTiers(standardPlans), [standardPlans]);
    // Resync de los cortes con el server (tras publicar segmentación). Deps = [serverCuts] estable.
    useEffect(() => {
        setSegCuts(serverCuts);
    }, [serverCuts]);

    const affectedPlans = plansAffectedBy(segCuts, plans);
    // El motor decide el nivel del cliente; los planes deciden si lo aceptan. Desalineados, hay una
    // banda de SCI donde la venta muere con 400 al originar.
    const engineMisaligned = engineCuts !== null && !sameCuts(engineCuts, serverCuts);

    const publishSeg = (): void => {
        const edits: SegmentEdit[] = affectedPlans.map((p) => ({
            planId: p.planId,
            minSciScore: mapping[p.tier].min,
            maxSciScore: mapping[p.tier].max,
        }));
        segMut.mutate(
            { edits, cuts: segCuts, reason },
            {
                onSuccess: (result) => {
                    // Con parámetros fallidos el diálogo queda abierto: el motor no se movió.
                    if (result.params.failed.length === 0) closeConfirm();
                },
            },
        );
    };

    // ---- Plan seleccionado (el draft lo resincroniza el efecto de [serverPlan]) ----
    const selectPlan = (planId: string): void => {
        setSelectedPlanId(planId);
    };

    const publishPlan = (): void => {
        // `serverPlan.active` y no `draft`: un refetch puede traer el plan INACTIVE con el diálogo
        // ya abierto, y publicarlo dejaría una versión y un asiento sobre un plan que nadie ofrece.
        if (!draft?.planId || !serverPlan?.active) return;
        updateMut.mutate(
            { planId: draft.planId, plan: draft, reason },
            {
                onSuccess: () => {
                    closeConfirm();
                },
            },
        );
    };

    const discardPlan = (): void => {
        if (!serverPlan) return;
        setDraft({ ...serverPlan, termsWeeks: [...serverPlan.termsWeeks] });
        setResetCount((c) => c + 1);
    };

    // ---- Lista ordenada ----
    const sorted = [...standardPlans].sort((a, b) => {
        const val = (p: CreditPlan): number => {
            switch (sort.col) {
                case 'tier':
                    return TIER_ORDER[p.tier] ?? 0;
                case 'tna':
                    return p.tna;
                case 'amount':
                    return p.maxAmount;
                case 'version':
                    return p.version;
            }
        };
        return (val(a) - val(b)) * (sort.dir === 'asc' ? 1 : -1);
    });

    const toggleSort = (col: SortCol): void => {
        setSort((s) =>
            s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' },
        );
    };

    const sortArrow = (col: SortCol): string => {
        if (sort.col !== col) return '';
        return sort.dir === 'asc' ? '▲' : '▼';
    };

    const th = (col: SortCol, label: string, num = false): React.ReactNode => (
        <th
            onClick={() => {
                toggleSort(col);
            }}
            className={cn(
                'cursor-pointer select-none bg-muted/40 px-2.5 py-1.5 text-[9px] font-bold uppercase tracking-wide text-muted-foreground hover:text-foreground',
                num ? 'text-right' : 'text-left',
            )}
        >
            {label} {sortArrow(col)}
        </th>
    );

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <SciSegmentBar
                cuts={segCuts}
                dirty={segDirty || engineMisaligned}
                affectedCount={affectedPlans.length}
                onCutsChange={setSegCuts}
                onPublish={() => {
                    setConfirm('seg');
                }}
                onDiscard={() => {
                    setSegCuts(serverCuts);
                }}
            />

            {/* Sin plan para un nivel, su corte lo dibuja un valor de relleno — no configuración. */}
            {tiersSinPlan.length > 0 ? (
                <div className="flex-none border-b border-warning/40 bg-warning/10 px-4 py-1.5 text-[10.5px] text-warning">
                    El catálogo no tiene plan para{' '}
                    <b className="font-semibold">
                        {tiersSinPlan.map((tier) => TIER_LABEL[tier]).join(' ni ')}
                    </b>
                    . Los cortes de esos niveles salen de un valor de relleno, no del catálogo.
                </div>
            ) : null}

            {engineCuts !== null && engineMisaligned ? (
                <div className="flex-none border-b border-warning/40 bg-warning/10 px-4 py-1.5 text-[10.5px] text-warning">
                    Los cortes del motor de score no coinciden con los de los planes: el motor usa{' '}
                    <b className="font-semibold tabular-nums">{cutsLabel(engineCuts)}</b> y los
                    planes <b className="font-semibold tabular-nums">{cutsLabel(serverCuts)}</b>. Un
                    cliente puede caer en un nivel cuyo plan lo rechaza al originar. Publica la
                    segmentación para alinearlos.
                </div>
            ) : null}

            <MasterDetail
                list={
                    <div className="min-h-0 flex-1 overflow-y-auto">
                        <table className="w-full border-collapse text-[11px]">
                            <thead>
                                <tr>
                                    {th('tier', 'Plan')}
                                    {th('tna', 'TNA', true)}
                                    {th('amount', 'Monto máx', true)}
                                    <th className="bg-muted/40 px-2.5 py-1.5 text-left text-[9px] font-bold uppercase tracking-wide text-muted-foreground">
                                        Abono mín.
                                    </th>
                                    {th('version', 'Versión')}
                                </tr>
                            </thead>
                            <tbody>
                                {sorted.map((plan) => (
                                    <tr
                                        key={plan.planId}
                                        onClick={() => {
                                            selectPlan(plan.planId);
                                        }}
                                        className={cn(
                                            'cursor-pointer transition-colors',
                                            plan.planId === selectedPlanId
                                                ? 'bg-info/10'
                                                : 'hover:bg-accent/40',
                                        )}
                                    >
                                        <td className="border-b border-border/50 px-2.5 py-[7px]">
                                            <TierPill level={planTierToLevel(plan.tier)} />
                                            {plan.active ? null : (
                                                <span className="ml-1.5 rounded bg-muted px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-muted-foreground">
                                                    Inactivo
                                                </span>
                                            )}
                                        </td>
                                        <td className="border-b border-border/50 px-2.5 py-[7px] text-right tabular-nums">
                                            {Math.round(plan.tna * 100)}%
                                        </td>
                                        <td className="border-b border-border/50 px-2.5 py-[7px] text-right tabular-nums">
                                            {formatMXN(plan.maxAmount)}
                                        </td>
                                        <td className="border-b border-border/50 px-2.5 py-[7px] tabular-nums">
                                            {prepaymentMinimumLabel(plan.prepaymentMinimum)}
                                        </td>
                                        <td className="border-b border-border/50 px-2.5 py-[7px] text-[10px] text-muted-foreground">
                                            v{plan.version} · {formatDateMx(plan.lastModified)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                }
                detail={
                    draft && serverPlan ? (
                        <PlanDetail
                            key={`${selectedPlanId ?? ''}-${String(resetCount)}`}
                            plan={draft}
                            dirty={dirty}
                            onChange={setDraft}
                            onPublish={() => {
                                setConfirm('plan');
                            }}
                            onDiscard={discardPlan}
                        />
                    ) : (
                        <MasterDetailEmpty
                            icon={<Wrench className="size-10" aria-hidden />}
                            title="Selecciona un plan"
                            sub={
                                <>
                                    Elige una fila de la izquierda para ver o editar
                                    <br />
                                    las condiciones del plan del nivel.
                                </>
                            }
                        />
                    )
                }
            />

            <ConfirmDialog
                open={confirm === 'plan'}
                onOpenChange={(open) => {
                    if (!open) {
                        closeConfirm();
                        updateMut.reset();
                    }
                }}
                title={`Publicar plan · ${draft ? TIER_LABEL[draft.tier] : ''}`}
                confirmLabel="Publicar nueva versión"
                loading={updateMut.isPending}
                onConfirm={publishPlan}
                description={
                    draft ? (
                        <span className="block space-y-2 text-left">
                            <span className="block">
                                Los cambios crearán una nueva versión del plan{' '}
                                {TIER_LABEL[draft.tier]}. Se registrarán en la auditoría y aplicarán
                                a créditos originados a partir de la publicación. Los créditos ya
                                firmados conservan sus condiciones actuales.
                            </span>
                            <span className="block rounded bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                                Versión actual: v{draft.version} → el backend asignará la siguiente.
                            </span>
                        </span>
                    ) : null
                }
            >
                <div className="space-y-2">
                    <AuditReasonField
                        value={reason}
                        disabled={updateMut.isPending}
                        onChange={setReason}
                    />
                    {updateMut.isError ? (
                        <p className="rounded bg-destructive/10 px-3 py-2 text-xs text-destructive">
                            No se pudo publicar: {userMessageOf(updateMut.error)}
                        </p>
                    ) : null}
                </div>
            </ConfirmDialog>

            <ConfirmDialog
                open={confirm === 'seg'}
                onOpenChange={(open) => {
                    if (!open) {
                        closeConfirm();
                        segMut.reset();
                    }
                }}
                title="Publicar segmentación"
                confirmLabel="Publicar segmentación"
                loading={segMut.isPending}
                onConfirm={publishSeg}
                description={
                    <span className="block space-y-2 text-left">
                        <span className="block">
                            {affectedPlans.length === 0
                                ? 'Ningún plan cambia de rango con estos cortes.'
                                : `Los cortes SCI se aplicarán a los ${String(affectedPlans.length)} planes afectados (un guardado por plan).`}{' '}
                            Se registrará en auditoría y aplicará a créditos originados a partir de
                            la publicación.
                        </span>
                        <span className="block">
                            Además de los planes se actualizarán los tres cortes del motor de score
                            (elegibilidad, Plata y Oro), para que el nivel que asigna el motor y el
                            plan que lo acepta digan lo mismo.
                        </span>
                        <span className="block rounded bg-muted/40 px-3 py-2 text-xs tabular-nums text-muted-foreground">
                            Bronce {segCuts.start}–{segCuts.bp - 1} · Plata {segCuts.bp}–
                            {segCuts.po - 1} · Oro {segCuts.po}–{mapping.oro.max}
                        </span>
                    </span>
                }
            >
                <div className="space-y-2">
                    {/* El mismo motivo viaja a los planes Y a los cortes del motor de score. */}
                    <AuditReasonField
                        value={reason}
                        disabled={segMut.isPending}
                        onChange={setReason}
                    />
                    {segMut.isError ? (
                        <p className="rounded bg-destructive/10 px-3 py-2 text-xs text-destructive">
                            No se pudo publicar: {userMessageOf(segMut.error)}
                        </p>
                    ) : null}
                    {segMut.isSuccess && segMut.data.params.failed.length > 0 ? (
                        <p className="rounded bg-warning/10 px-3 py-2 text-xs text-warning">
                            Los planes se guardaron, pero los cortes del motor de score no:{' '}
                            {segMut.data.params.failed
                                .map((f) => `${f.key} (${f.reason})`)
                                .join(' · ')}
                            . El motor sigue usando los cortes anteriores; vuelve a publicar o
                            corrígelos en Configuración global.
                        </p>
                    ) : null}
                </div>
            </ConfirmDialog>
        </div>
    );
}

/**
 * Motivo del cambio: opcional, y queda escrito en la columna Motivo de la bitácora. El tope de
 * caracteres es el del backend — cortar aquí evita que el operador lo descubra al publicar.
 */
function AuditReasonField({
    value,
    disabled,
    onChange,
}: {
    value: string;
    disabled: boolean;
    onChange: (value: string) => void;
}) {
    const fieldId = useId();
    return (
        <div className="space-y-1 text-left">
            <label htmlFor={fieldId} className="block text-xs font-medium text-foreground">
                Motivo (opcional)
            </label>
            <Textarea
                id={fieldId}
                value={value}
                rows={2}
                maxLength={AUDIT_REASON_MAX_LENGTH}
                disabled={disabled}
                className="min-h-0 text-xs"
                placeholder="Ej.: ajuste trimestral de tasa autorizado por el Comité."
                onChange={(event) => {
                    onChange(event.target.value);
                }}
            />
            <p className="text-right text-[10px] tabular-nums text-muted-foreground">
                {value.length}/{AUDIT_REASON_MAX_LENGTH}
            </p>
        </div>
    );
}
